import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowUpRight, 
  ShoppingCart,
  Globe,
  Target,
  TrendingUp,
  Package,
  BarChart3,
  Zap,
  Settings,
  LineChart,
  Megaphone,
  Store,
  Layers
} from 'lucide-react';

const ServicesPage = () => {
  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const services = [
    {
      id: 'marketplace',
      icon: <ShoppingCart className="w-8 h-8" />,
      title: 'Consulenza Marketplace',
      description: 'Scala la tua presenza sui principali marketplace con strategie collaudate e tecniche di ottimizzazione.',
      features: [
        {
          icon: <Package className="w-5 h-5" />,
          title: 'Strategia di Crescita eBay',
          description: 'Sviluppa strategie complete per massimizzare la tua presenza e performance di vendita su eBay.'
        },
        {
          icon: <Store className="w-5 h-5" />,
          title: 'Ottimizzazione Amazon & Marketplace',
          description: 'Ottimizza listing, prezzi e operazioni su Amazon e altre piattaforme.'
        },
        {
          icon: <BarChart3 className="w-5 h-5" />,
          title: 'Ottimizzazione Listing',
          description: 'Crea listing ad alta conversione con titoli, descrizioni e immagini ottimizzate.'
        },
        {
          icon: <TrendingUp className="w-5 h-5" />,
          title: 'Scaling Marketplace',
          description: 'Costruisci sistemi e processi per scalare il tuo business marketplace in modo sostenibile.'
        }
      ]
    },
    {
      id: 'ecommerce',
      icon: <Globe className="w-8 h-8" />,
      title: 'Consulenza Ecommerce',
      description: 'Costruisci e ottimizza la tua presenza ecommerce per massimizzare conversioni e crescita sostenibile.',
      features: [
        {
          icon: <Store className="w-5 h-5" />,
          title: 'Setup & Ottimizzazione Shopify',
          description: 'Lancia e ottimizza il tuo negozio Shopify con best practice e framework collaudati.'
        },
        {
          icon: <Zap className="w-5 h-5" />,
          title: 'Ottimizzazione Conversioni',
          description: 'Migliora i tuoi tassi di conversione attraverso test e ottimizzazione data-driven.'
        },
        {
          icon: <Layers className="w-5 h-5" />,
          title: 'Strategia Prodotto',
          description: 'Sviluppa strategie di prodotto vincenti che risuonano con il tuo target audience.'
        },
        {
          icon: <Settings className="w-5 h-5" />,
          title: 'Sistemi di Scaling Ecommerce',
          description: 'Costruisci sistemi scalabili per inventario, fulfillment e customer service.'
        }
      ]
    },
    {
      id: 'marketing',
      icon: <Target className="w-8 h-8" />,
      title: 'Consulenza Digital Marketing',
      description: 'Accelera la crescita con strategie di performance marketing sui canali social e digitali.',
      features: [
        {
          icon: <Megaphone className="w-5 h-5" />,
          title: 'Strategia Meta Ads',
          description: 'Crea campagne pubblicitarie ad alte performance su Facebook e Instagram.'
        },
        {
          icon: <Zap className="w-5 h-5" />,
          title: 'Strategia TikTok Ads',
          description: 'Sfrutta la potente piattaforma TikTok per raggiungere nuove audience e aumentare le vendite.'
        },
        {
          icon: <LineChart className="w-5 h-5" />,
          title: 'Performance Marketing',
          description: 'Strategie di marketing data-driven focalizzate su ROAS e crescita misurabile.'
        },
        {
          icon: <BarChart3 className="w-5 h-5" />,
          title: 'Analytics & Reporting',
          description: 'Traccia, misura e ottimizza le performance del tuo marketing con chiarezza.'
        }
      ]
    },
    {
      id: 'growth',
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Consulenza Crescita Aziendale',
      description: 'Pianificazione strategica ed esecuzione per scalare i tuoi ricavi e costruire una crescita sostenibile.',
      features: [
        {
          icon: <Target className="w-5 h-5" />,
          title: 'Strategia Digitale',
          description: 'Sviluppa strategie digitali complete allineate ai tuoi obiettivi di business.'
        },
        {
          icon: <LineChart className="w-5 h-5" />,
          title: 'Pianificazione Crescita Ricavi',
          description: 'Crea piani azionabili per aumentare i ricavi e migliorare la profittabilità.'
        },
        {
          icon: <Settings className="w-5 h-5" />,
          title: 'Ottimizzazione Processi',
          description: 'Snellisci le operazioni ed elimina le inefficienze per migliori performance.'
        },
        {
          icon: <Layers className="w-5 h-5" />,
          title: 'Sistemi di Scaling',
          description: 'Costruisci l\'infrastruttura e i sistemi necessari per scalare il tuo business.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#2DD4BF]/5 rounded-full blur-[100px]" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <span className="text-[#2DD4BF] text-sm font-semibold tracking-wider uppercase mb-4 block">
              I Nostri Servizi
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A1D24] leading-tight tracking-tight mb-6">
              Consulenza Strategica per la Crescita Digitale
            </h1>
            <p className="text-lg text-[#4B5563] leading-relaxed">
              Dall'espansione marketplace all'ottimizzazione ecommerce, forniamo servizi di consulenza completi per aiutare il tuo business a scalare.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      {services.map((service, serviceIndex) => (
        <section
          key={service.id}
          id={service.id}
          className={`py-24 md:py-32 ${serviceIndex % 2 === 0 ? 'bg-white' : 'bg-[#F4F5F7]'}`}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div {...fadeUp} className="mb-16">
              <div className="flex items-center gap-6 mb-6">
                <div className="w-16 h-16 rounded-lg bg-[#2DD4BF]/10 flex items-center justify-center text-[#2DD4BF]">
                  {service.icon}
                </div>
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold text-[#1A1D24] tracking-tight">
                    {service.title}
                  </h2>
                </div>
              </div>
              <p className="text-[#4B5563] text-lg max-w-2xl leading-relaxed">
                {service.description}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              {service.features.map((feature, featureIndex) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: featureIndex * 0.1 }}
                  data-testid={`service-feature-${service.id}-${featureIndex}`}
                  className="bg-[#F9FAFB] border border-[#E5E7EB] p-8 rounded-lg hover:border-[#2DD4BF]/50 hover:shadow-lg transition-all group"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[#2DD4BF]/10 flex items-center justify-center text-[#2DD4BF] flex-shrink-0 group-hover:bg-[#2DD4BF]/20 transition-colors">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-[#1A1D24] mb-2 group-hover:text-[#2DD4BF] transition-colors">
                        {feature.title}
                      </h3>
                      <p className="text-[#6B7280] leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="mt-12 pt-8 border-t border-[#E5E7EB]"
            >
              <Link
                to="/contact"
                data-testid={`service-cta-${service.id}`}
                className="inline-flex items-center gap-2 text-[#2DD4BF] font-medium hover:gap-3 transition-all"
              >
                Parliamo di {service.title}
                <ArrowUpRight size={18} />
              </Link>
            </motion.div>
          </div>
        </section>
      ))}

      {/* Process Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <span className="text-[#2DD4BF] text-sm font-semibold tracking-wider uppercase mb-4 block">
              Il Nostro Processo
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A1D24] tracking-tight">
              Come Lavoriamo con Te
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', description: 'Iniziamo comprendendo il tuo business, obiettivi e sfide.' },
              { step: '02', title: 'Strategia', description: 'Sviluppiamo una strategia personalizzata su misura per le tue esigenze specifiche.' },
              { step: '03', title: 'Esecuzione', description: 'Implementiamo la strategia con precisione e attenzione ai dettagli.' },
              { step: '04', title: 'Ottimizzazione', description: 'Misuriamo, analizziamo e miglioriamo continuamente le performance.' }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                <div className="text-6xl font-bold text-[#2DD4BF]/10 mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-[#1A1D24] mb-2">
                  {item.title}
                </h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">
                  {item.description}
                </p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 right-0 w-full h-px bg-gradient-to-r from-[#2DD4BF]/20 to-transparent" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-[#F4F5F7] to-[#E8E9EB] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#2DD4BF]/5 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div {...fadeUp} className="text-center">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1D24] tracking-tight mb-6">
              Pronto per Iniziare?
            </h2>
            <p className="text-[#6B7280] text-lg max-w-xl mx-auto mb-10">
              Parliamo di come possiamo aiutarti a raggiungere i tuoi obiettivi di crescita con un progetto di consulenza personalizzato.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                data-testid="services-cta-consultation"
                className="inline-flex items-center gap-2 bg-[#2DD4BF] text-white px-8 py-4 font-semibold rounded-sm hover:bg-[#14B8A6] transition-all hover:scale-105 shadow-lg"
              >
                Richiedi una Call Strategica
                <ArrowUpRight size={20} />
              </Link>
              <Link
                to="/about"
                data-testid="services-cta-about"
                className="inline-flex items-center gap-2 bg-white border border-[#E5E7EB] text-[#1A1D24] px-8 py-4 font-semibold rounded-sm hover:bg-[#F9FAFB] transition-all shadow-sm"
              >
                Scopri il Nostro Approccio
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
